import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';
import communicationService from '../../../services/communications';
import { useAuth } from '../../../contexts/AuthContext';

const CommunicationHistory = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  // Channel and status options
  const channelOptions = [
    { value: '', label: 'All Channels' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'call', label: 'Phone Call' },
    { value: 'social_message', label: 'Social Media' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'opened', label: 'Opened' },
    { value: 'clicked', label: 'Clicked' },
    { value: 'failed', label: 'Failed' }
  ];

  // Fetch messages when component mounts
  useEffect(() => {
    if (user) {
      fetchMessages();
    }
  }, [user]);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [messages, searchQuery, filterChannel, filterStatus]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await communicationService?.getMessages({ limit: 50 });
      
      if (response?.success) {
        setMessages(response?.data || []);
      } else {
        setError(response?.error || 'Failed to fetch communication history');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load communication history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...messages];

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      filtered = filtered?.filter(message =>
        message?.subject?.toLowerCase()?.includes(searchLower) ||
        message?.content?.toLowerCase()?.includes(searchLower) ||
        message?.contact?.full_name?.toLowerCase()?.includes(searchLower) ||
        message?.contact?.name?.toLowerCase()?.includes(searchLower) ||
        message?.contact?.email?.toLowerCase()?.includes(searchLower) ||
        message?.contact?.company?.toLowerCase()?.includes(searchLower)
      );
    }

    // Apply channel filter
    if (filterChannel) {
      filtered = filtered?.filter(message => message?.type === filterChannel);
    }

    // Apply status filter  
    if (filterStatus) {
      filtered = filtered?.filter(message => message?.status === filterStatus);
    }

    setFilteredMessages(filtered);
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return 'Mail';
      case 'sms':
        return 'MessageSquare';
      case 'call':
        return 'Phone';
      case 'social_message':
        return 'Share2';
      default:
        return 'MessageCircle';
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case 'email':
        return 'text-primary';
      case 'sms':
        return 'text-secondary';
      case 'call':
        return 'text-accent';
      case 'social_message':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning';
      case 'sent':
        return 'text-secondary';
      case 'delivered':
        return 'text-primary';
      case 'opened':
        return 'text-info';
      case 'clicked':
        return 'text-success';
      case 'failed':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'Clock';
      case 'sent':
        return 'Send';
      case 'delivered':
        return 'Check';
      case 'opened':
        return 'Eye';
      case 'clicked':
        return 'MousePointer';
      case 'failed':
        return 'X';
      default:
        return 'Clock';
    }
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
  };

  const closeMessageModal = () => {
    setSelectedMessage(null);
    setShowMessageModal(false);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp)?.toLocaleString();
    } catch (err) {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">Error Loading Messages</h4>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchMessages} iconName="RotateCcw" iconPosition="left">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      {/* Header with Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <h3 className="text-lg font-semibold text-foreground">Communication History</h3>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Input
              type="search"
              placeholder="Search communications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="sm:w-64"
            />
            <Select
              placeholder="Channel"
              options={channelOptions}
              value={filterChannel}
              onChange={setFilterChannel}
              className="sm:w-40"
            />
            <Select
              placeholder="Status"
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              className="sm:w-40"
            />
          </div>
        </div>
      </div>
      {/* Communication Timeline */}
      <div className="max-h-96 overflow-y-auto">
        {filteredMessages?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="MessageCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">
              {messages?.length === 0 ? 'No communications found' : 'No communications match your filters'}
            </h4>
            <p className="text-muted-foreground">
              {messages?.length === 0 
                ? 'Start communicating with your contacts to see history here' :'Try adjusting your search criteria or filters'
              }
            </p>
            {messages?.length === 0 && (
              <Button 
                onClick={fetchMessages} 
                iconName="RotateCcw" 
                iconPosition="left"
                className="mt-4"
              >
                Refresh
              </Button>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {filteredMessages?.map((message) => {
              const contactName = message?.contact?.full_name || message?.contact?.name || message?.recipient || 'Unknown Contact';
              const isInbound = message?.direction === 'inbound';
              
              return (
                <div
                  key={message?.id}
                  className={`p-4 border border-border rounded-lg hover:bg-muted cursor-pointer transition-colors duration-200 ${
                    isInbound ? 'bg-primary/5 border-primary/20' : ''
                  }`}
                  onClick={() => openMessageModal(message)}
                >
                  <div className="flex items-start space-x-4">
                    {/* Contact Avatar */}
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                      {message?.contact?.avatar ? (
                        <Image
                          src={message?.contact?.avatar}
                          alt={contactName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="User" size={16} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Communication Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-foreground">
                            {contactName}
                          </h4>
                          {message?.contact?.company && (
                            <>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground">
                                {message?.contact?.company}
                              </span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatTimestamp(message?.created_at)}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2 mb-2">
                        <Icon
                          name={getChannelIcon(message?.type)}
                          size={14}
                          className={getChannelColor(message?.type)}
                        />
                        <span className="text-sm font-medium text-foreground">
                          {message?.subject || `${message?.type?.replace('_', ' ')?.toUpperCase()} Message`}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isInbound ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary'
                        }`}>
                          {isInbound ? 'inbound' : 'outbound'}
                        </span>
                      </div>

                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {message?.content || message?.message || 'No content available'}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Icon
                            name={getStatusIcon(message?.status)}
                            size={14}
                            className={getStatusColor(message?.status)}
                          />
                          <span className={`text-xs font-medium ${getStatusColor(message?.status)}`}>
                            {message?.status?.charAt(0)?.toUpperCase() + message?.status?.slice(1)}
                          </span>
                        </div>

                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" iconName="Eye" iconSize={12}>
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Footer */}
      {filteredMessages?.length > 0 && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {filteredMessages?.length} of {messages?.length} communications
            </span>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={fetchMessages} iconName="RotateCcw" iconSize={12}>
                Refresh
              </Button>
              {messages?.length >= 50 && (
                <Button variant="outline" size="sm">
                  Load More
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Message Details Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">Message Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeMessageModal}
                iconName="X"
              />
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-card-foreground">Type</label>
                  <p className="text-sm text-muted-foreground capitalize">
                    {selectedMessage?.type?.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-card-foreground">Status</label>
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={getStatusIcon(selectedMessage?.status)} 
                      size={14} 
                      className={getStatusColor(selectedMessage?.status)} 
                    />
                    <span className="text-sm text-muted-foreground capitalize">
                      {selectedMessage?.status}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-card-foreground">Recipient</label>
                  <p className="text-sm text-muted-foreground">
                    {selectedMessage?.contact?.full_name || selectedMessage?.contact?.name || selectedMessage?.recipient}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-card-foreground">Date</label>
                  <p className="text-sm text-muted-foreground">
                    {formatTimestamp(selectedMessage?.created_at)}
                  </p>
                </div>
              </div>
              
              {selectedMessage?.subject && (
                <div>
                  <label className="text-sm font-medium text-card-foreground">Subject</label>
                  <p className="text-sm text-muted-foreground">{selectedMessage?.subject}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-card-foreground">Content</label>
                <div className="mt-2 p-4 bg-muted rounded-lg">
                  <p className="text-sm text-card-foreground whitespace-pre-wrap">
                    {selectedMessage?.content || selectedMessage?.message || 'No content available'}
                  </p>
                </div>
              </div>
              
              {selectedMessage?.metadata && Object.keys(selectedMessage?.metadata)?.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-card-foreground">Additional Details</label>
                  <div className="mt-2 space-y-2">
                    {Object.entries(selectedMessage?.metadata)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{key?.replace('_', ' ')}:</span>
                        <span className="text-card-foreground">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationHistory;